import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RemediationActionsList = ({ actions, onUpdateStatus, loading }) => {
  const getPriorityColor = (priority) => {
    const colors = {
      immediate: 'text-error',
      next_pay_period: 'text-warning',
      next_month: 'text-primary',
      scheduled: 'text-info'
    };
    return colors?.[priority] || 'text-muted-foreground';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-warning',
      in_progress: 'text-primary',
      completed: 'text-success',
      failed: 'text-error',
      cancelled: 'text-muted-foreground'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'K0.00';
    return `K${amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-PG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
      </div>
    );
  }

  if (!actions || actions?.length === 0) {
    return (
      <div className="py-12 text-center">
        <Icon name="CheckSquare" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
        <p className="caption text-sm text-muted-foreground">No remediation actions available</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {actions?.map((action) => (
        <div key={action?.id} className="border border-border rounded-lg p-4 hover:shadow-sm transition-smooth">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Icon name="CheckSquare" size={18} color="var(--color-primary)" />
              <h4 className="font-heading font-semibold text-sm text-foreground capitalize">
                {action?.actionType?.replace(/_/g, ' ')}
              </h4>
            </div>
            <span className={`px-2 py-1 bg-muted ${getStatusColor(action?.remediationStatus)} text-xs font-caption rounded-full capitalize`}>
              {action?.remediationStatus?.replace(/_/g, ' ')}
            </span>
          </div>

          <p className="caption text-sm text-muted-foreground mb-3">{action?.description}</p>

          {action?.violation && (
            <div className="bg-muted/50 rounded-lg p-3 mb-3">
              <div className="flex items-center gap-2 mb-1">
                <Icon name="AlertTriangle" size={14} color="var(--color-warning)" />
                <p className="caption text-xs font-medium text-foreground">Related Violation</p>
              </div>
              <p className="caption text-xs text-muted-foreground capitalize">
                {action?.violation?.violationType?.replace(/_/g, ' ')} ({action?.violation?.severity})
              </p>
            </div>
          )}

          {action?.implementationSteps && action?.implementationSteps?.length > 0 && (
            <div className="mb-3">
              <p className="caption text-xs font-medium text-foreground mb-2">Implementation Steps</p>
              <ol className="list-decimal list-inside space-y-1">
                {action?.implementationSteps?.map((step, index) => (
                  <li key={index} className="caption text-xs text-muted-foreground">
                    {step}
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div>
              <p className="caption text-xs text-muted-foreground mb-1">Priority</p>
              <p className={`font-heading font-medium text-sm capitalize ${getPriorityColor(action?.priority)}`}>
                {action?.priority?.replace(/_/g, ' ')}
              </p>
            </div>
            <div>
              <p className="caption text-xs text-muted-foreground mb-1">Backpay Amount</p>
              <p className="font-heading font-medium text-sm text-warning">
                {formatCurrency(action?.backpayAmount)}
              </p>
            </div>
            <div>
              <p className="caption text-xs text-muted-foreground mb-1">Affected</p>
              <p className="font-heading font-medium text-sm text-foreground">
                {action?.affectedCount} employees
              </p>
            </div>
            <div>
              <p className="caption text-xs text-muted-foreground mb-1">Scheduled Date</p>
              <p className="caption text-xs text-foreground">
                {formatDate(action?.scheduledDate)}
              </p>
            </div>
          </div>

          {action?.assignedTo && (
            <div className="flex items-center gap-2 mb-3">
              <Icon name="User" size={14} color="var(--color-muted-foreground)" />
              <p className="caption text-xs text-muted-foreground">
                Assigned to: <span className="font-medium text-foreground">{action?.assignedTo}</span>
              </p>
            </div>
          )}

          {action?.notes && (
            <p className="caption text-xs text-muted-foreground mb-3 italic">
              Note: {action?.notes}
            </p>
          )}

          <div className="flex items-center gap-2">
            {action?.remediationStatus === 'pending' && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => onUpdateStatus?.(action?.id, 'in_progress')}
              >
                Start Action
              </Button>
            )}
            {action?.remediationStatus === 'in_progress' && (
              <Button
                variant="success"
                size="sm"
                onClick={() => onUpdateStatus?.(action?.id, 'completed', { notes: 'Remediation completed successfully' })}
              >
                Mark Complete
              </Button>
            )}
            {action?.remediationStatus === 'completed' && action?.completedAt && (
              <div className="flex items-center gap-2 text-success">
                <Icon name="CheckCircle2" size={16} color="currentColor" />
                <p className="caption text-xs">
                  Completed on {formatDate(action?.completedAt)}
                </p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default RemediationActionsList;